import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../../services/apiClient';

const PaymentCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing your payment...');
  const hasProcessed = useRef(false); // Prevent duplicate API calls

  useEffect(() => {
    // Prevent duplicate processing
    if (hasProcessed.current) {
      return;
    }

    const processPayment = async () => {
      hasProcessed.current = true; // Mark as processed immediately

      const provider = searchParams.get('provider');
      const sessionId = searchParams.get('session_id');
      const token = searchParams.get('token');
      const payerId = searchParams.get('PayerID');

      try {
        let response;

        if (provider === 'stripe' && sessionId) {
          // Call Stripe success endpoint
          response = await apiClient.get(`/payments/stripe/success?session_id=${sessionId}`);
        } else if (provider === 'paypal' && token && payerId) {
          // Call PayPal success endpoint
          response = await apiClient.get(`/payments/paypal/success?token=${token}&PayerID=${payerId}`);
        } else {
          throw new Error('Invalid payment parameters');
        }

        if (response.success && response.data?.bookingToken) {
          setStatus('success');
          setMessage('Payment successful! Redirecting to booking confirmation...');

          // Clear pending lock from localStorage on success
          localStorage.removeItem('pending_booking_token');
          localStorage.removeItem('pending_booking_time');
          console.log('🧹 [Frontend] Payment successful, cleared pending lock from localStorage');

          // Redirect to booking confirmation page
          setTimeout(() => {
            navigate(`/booking-confirmation/${response.data.bookingToken}`, { replace: true });
          }, 2000);
        } else {
          throw new Error(response.message || 'Payment processing failed');
        }
      } catch (error) {
        console.error('Payment callback error:', error);
        setStatus('error');

        // If it's a 401/403 (session expired or user mismatch), redirect to login
        // preserving the callback URL so they can complete payment after re-auth
        if (error.status === 401 || error.status === 403) {
          const currentUrl = window.location.pathname + window.location.search;
          sessionStorage.setItem('redirectAfterLogin', currentUrl);
          setMessage('Your session expired during payment. Please log in again to complete your booking.');
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 3000);
          return;
        }

        setMessage(error.response?.data?.message || error.message || 'Payment processing failed');

        // Redirect to home page after 5 seconds
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 5000);
      }
    };

    processPayment();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8">
        <div className="text-center">
          {status === 'processing' && (
            <>
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-500 mx-auto mb-4"></div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Processing Payment</h2>
              <p className="text-gray-600">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="text-green-500 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Payment Successful!</h2>
              <p className="text-gray-600">{message}</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="text-red-500 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Payment Failed</h2>
              <p className="text-gray-600 mb-4">{message}</p>
              <button
                onClick={() => navigate('/', { replace: true })}
                className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-2 px-6 rounded transition-colors duration-200"
              >
                Return to Home
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentCallbackPage;
